import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, KanbanSquare, Users, FileText, UserSearch, MessageSquareWarning, ShieldCheck,
  FileBadge, Scale, IndianRupee, Handshake, ClipboardList, UserPlus, Mail, Landmark, Tag,
  Newspaper, Bot, UsersRound, Lock, MessageSquareText, History, KeyRound, Menu, X, LogOut,
  ExternalLink, ChevronsLeft, ChevronsRight,
} from "lucide-react";
import Logo from "@/components/Logo";
import { currentUser, tokenStorage } from "@/lib/api";
import { DeliveryAlertsBell } from "@/components/admin/DeliveryAlertsBell";
import { EmailQuotaChip } from "@/components/admin/EmailQuotaChip";

const NAV = [
  { title: "Overview", items: [
    { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/pipeline", label: "Pipeline", icon: KanbanSquare },
  ]},
  { title: "CRM", items: [
    { to: "/admin/leads", label: "Leads", icon: Users },
    { to: "/admin/applications", label: "Applications", icon: FileText },
    { to: "/admin/customer", label: "Customer 360", icon: UserSearch },
    { to: "/admin/grievances", label: "Grievances", icon: MessageSquareWarning },
  ]},
  { title: "Credit", items: [
    { to: "/admin/eligibility-checks", label: "Eligibility Checks", icon: ShieldCheck },
    { to: "/admin/letters-issued", label: "Letters Issued", icon: FileBadge },
    { to: "/admin/credit-policies", label: "Credit Policies", icon: Scale },
    { to: "/admin/letter-qa", label: "Letter QA (v1.1)", icon: ClipboardList },
    { to: "/admin/integrations", label: "Integrations · Bureau", icon: Landmark },
    { to: "/admin/manual-payments", label: "Manual Payments", icon: IndianRupee },
  ]},
  { title: "Partners", items: [
    { to: "/admin/partners", label: "Partners", icon: Handshake },
    { to: "/admin/registrations", label: "Registrations", icon: ClipboardList },
    { to: "/admin/onboarding", label: "Onboarding", icon: UserPlus },
    { to: "/admin/invitations", label: "Invitations", icon: Mail },
    { to: "/admin/investors", label: "Investors", icon: Landmark },
  ]},
  { title: "Content", items: [
    { to: "/admin/offers", label: "Hot Offers", icon: Tag },
    { to: "/admin/blogs", label: "Blogs", icon: Newspaper },
    { to: "/admin/ai-helper", label: "AI Helper", icon: Bot },
  ]},
  { title: "System", items: [
    { to: "/admin/staff", label: "Staff", icon: UsersRound },
    { to: "/admin/security", label: "Security", icon: Lock },
    { to: "/admin/sms/log", label: "SMS Log", icon: MessageSquareText },
    { to: "/admin/onboarding-log", label: "Onboarding Log", icon: History },
    { to: "/admin/change-password", label: "Change Password", icon: KeyRound },
  ]},
];

const RAIL_KEY = "sf_admin_rail";

const NavItems = ({ compact, onNavigate }) => (
  <nav aria-label="Admin" className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
    {NAV.map((g) => (
      <div key={g.title}>
        {!compact && <div className="px-3 mb-1 text-[9px] uppercase tracking-[0.24em] text-[#D4AF37]/80">{g.title}</div>}
        <ul className="space-y-0.5">
          {g.items.map(({ to, label, icon: Icon }) => (
            <li key={to}>
              <NavLink
                to={to}
                onClick={onNavigate}
                data-testid={`admin-nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
                title={label}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-lg px-3 min-h-[40px] text-[13px] transition-colors ${
                    isActive ? "bg-[#D4AF37]/15 text-[#F0D788] border border-[#D4AF37]/40" : "text-white/75 hover:bg-white/[0.06] hover:text-white border border-transparent"
                  } ${compact ? "justify-center px-0" : ""}`
                }
              >
                <Icon size={16} className="shrink-0" aria-hidden="true" />
                {!compact && <span className="truncate">{label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    ))}
  </nav>
);

/** Shared responsive admin layout: sidebar (desktop, collapsible rail) → drawer (mobile). */
export default function AdminShell() {
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(() => localStorage.getItem(RAIL_KEY) === "1");
  const loc = useLocation();
  const nav = useNavigate();
  const user = currentUser();

  useEffect(() => { setOpen(false); }, [loc.pathname]);
  useEffect(() => { localStorage.setItem(RAIL_KEY, compact ? "1" : "0"); }, [compact]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const logout = () => { tokenStorage.clear(); nav("/admin"); };
  const current = NAV.flatMap((g) => g.items).find((i) => loc.pathname.startsWith(i.to));

  const Brand = ({ small }) => (
    <div className="flex items-center gap-3 min-w-0">
      <Logo size={small ? 34 : 40} testId="admin-shell-logo" />
      {!small && (
        <div className="leading-tight min-w-0 hidden min-[360px]:block">
          <div className="font-display text-[12px] text-white uppercase tracking-tight whitespace-nowrap keep-nowrap truncate">Sahyog Finance</div>
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]">Admin Console</div>
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="admin-shell" className={`admin-shell min-h-screen bg-[#04102B] text-white lg:grid ${compact ? "lg:grid-cols-[72px_minmax(0,1fr)]" : "lg:grid-cols-[248px_minmax(0,1fr)]"}`}>
      {/* Desktop sidebar */}
      <aside data-testid="admin-sidebar" className="hidden lg:flex flex-col sticky top-0 h-screen border-r border-white/10 bg-[#030c22]">
        <div className={`h-16 flex items-center border-b border-white/10 ${compact ? "justify-center px-0" : "justify-between px-4"}`}>
          <Brand small={compact} />
          {!compact && (
            <div className="flex items-center gap-1">
            <DeliveryAlertsBell align="left" />
            <button data-testid="admin-rail-toggle" onClick={() => setCompact(true)} aria-label="Collapse sidebar" className="text-white/50 hover:text-white p-2 rounded-md">
              <ChevronsLeft size={16} />
            </button>
            </div>
          )}
        </div>
        {compact && (
          <button data-testid="admin-rail-expand" onClick={() => setCompact(false)} aria-label="Expand sidebar" className="mx-auto mt-2 text-white/50 hover:text-white p-2 rounded-md">
            <ChevronsRight size={16} />
          </button>
        )}
        <div className={`px-3 pt-3 ${compact ? "flex justify-center" : ""}`}><EmailQuotaChip compact={compact} /></div>
        <NavItems compact={compact} />
        <div className={`border-t border-white/10 p-3 ${compact ? "flex flex-col items-center gap-2" : ""}`}>
          {!compact && <div className="text-[11px] text-white/60 truncate mb-2" title={user?.email}>{user?.email} · <span className="uppercase text-[#D4AF37]">{user?.role}</span></div>}
          <div className={`flex ${compact ? "flex-col" : ""} gap-2`}>
            <a href="/" target="_blank" rel="noreferrer" title="View public site" className="inline-flex items-center justify-center gap-1.5 px-3 min-h-[36px] rounded-full border border-white/15 text-[11px] text-white/75 hover:border-[#D4AF37] flex-1">
              <ExternalLink size={12} />{!compact && "Site"}
            </a>
            <button data-testid="admin-shell-logout" onClick={logout} title="Log out" className="inline-flex items-center justify-center gap-1.5 px-3 min-h-[36px] rounded-full border border-rose-400/30 text-[11px] text-rose-200 hover:bg-rose-500/10 flex-1">
              <LogOut size={12} />{!compact && "Logout"}
            </button>
          </div>
        </div>
      </aside>

      <div className="min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div data-testid="admin-topbar" className="lg:hidden sticky top-0 z-50 h-14 flex items-center justify-between gap-3 px-3 border-b border-white/10 bg-[#030c22]/95 backdrop-blur">
          <button data-testid="admin-menu-toggle" onClick={() => setOpen(true)} aria-label="Open admin menu" className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-white/85 hover:bg-white/[0.06]">
            <Menu size={20} />
          </button>
          <div className="min-w-0 text-center">
            <div className="text-[9px] uppercase tracking-[0.2em] text-[#D4AF37]">Admin Console</div>
            <div className="text-sm text-white/90 truncate">{current?.label || "Sahyog Finance"}</div>
          </div>
          <div className="flex items-center gap-1"><EmailQuotaChip compact slot="topbar" /><DeliveryAlertsBell /><Logo size={34} testId="admin-topbar-logo" /></div>
        </div>

        {/* Drawer */}
        {open && (
          <div className="lg:hidden fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Admin navigation">
            <button aria-label="Close menu" onClick={() => setOpen(false)} className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div data-testid="admin-drawer" className="absolute inset-y-0 left-0 w-[min(84vw,300px)] bg-[#030c22] border-r border-white/10 flex flex-col shadow-2xl">
              <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
                <Brand />
                <button data-testid="admin-drawer-close" onClick={() => setOpen(false)} aria-label="Close menu" className="w-11 h-11 inline-flex items-center justify-center rounded-lg text-white/80"><X size={18} /></button>
              </div>
              <NavItems onNavigate={() => setOpen(false)} />
              <div className="border-t border-white/10 p-3 flex gap-2">
                <a href="/" className="flex-1 inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded-full border border-white/15 text-xs text-white/80"><ExternalLink size={12} /> Site</a>
                <button onClick={logout} className="flex-1 inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded-full border border-rose-400/30 text-xs text-rose-200"><LogOut size={12} /> Logout</button>
              </div>
            </div>
          </div>
        )}

        <main id="main-content" tabIndex={-1} className="admin-shell-content min-w-0 flex-1 outline-none">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
